import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";

import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { WinType } from "../../../abstracts/types/enums";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { addSkip } from "../../../abstracts/utils/JSUtils";
import { Apocalypse } from "../../Apocalypse";
import { fsConfig, getWinLevel, normalConfig } from "../../config/ApocalypseConfig";
import { SHAKE_NORMAL_SCREEN, SHOW_EASTER_EGG_ANIMATION } from "../../events/ApocalypseEvents";
import { ApocalypseReelsView } from "../../modules/Reels/ApocalypseReelsView";
import { ApocalypseNormalContainer } from "../../modules/Reels/normal/ApocalypseNormalContainer";
import { ApocalypseReel } from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseWildSymbol } from "../../modules/Reels/symbols/ApocalypseWildSymbol";
import { ApocalypseXNudgeSymbol } from "../../modules/Reels/symbols/ApocalypseXNudgeSymbol";
import { ApocalypseWinView } from "../../modules/Winnings/ApocalypseWinView";
import { ApocalypseSymbolNames } from "../../types/ApocalypseEnums";
import { ApocalypseResultDecorator } from "../decorators/ApocalypseResultDecorators";

export class ApocalypseNormalResultPresentation extends AbstractFlowPresentation {
    protected reelsView: ApocalypseReelsView;
    protected normalContainer: ApocalypseNormalContainer;
    protected winView: ApocalypseWinView;
    protected reels: ApocalypseReel[] = [];
    protected specialSymbols: (ApocalypseXNudgeSymbol | ApocalypseWildSymbol)[] = [];

    constructor(id: string = `UCNormalResultPresentation`) {
        super(id);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(ReelsEvents.GET_NORMAL_REELS, (normalReels: ApocalypseReel[]) => {
            this.reels = normalReels;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (view: ApocalypseReelsView) => {
            this.reelsView = view;
            this.normalContainer = this.reelsView.getNormalContainer();
        });

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (view: ApocalypseWinView) => {
            this.winView = view;
        });
    }

    protected onDefaultStart() {
        super.onDefaultStart();

        this.specialSymbols = [];

        const padding = Apocalypse.gameFlowController.calculatePaddingDuration(
            this.flowPresentationTimeline.totalDuration()
        );

        if (padding > 0) {
            gsap.delayedCall(padding, this.handleFinishAnimation.bind(this));
        } else {
            this.handleFinishAnimation();
        }
    }

    protected handleFinishAnimation(): void {
        this.flowPresentationTimeline = gsap.timeline({
            onStart: () => {},
        });

        ////////////////////////

        const stopDelay = Apocalypse.apiPlugin.settings.get(APISetting.FAST_SPIN, false)
            ? normalConfig.skipReelsDelay
            : normalConfig.stopSpinReelDelay;

        ApocalypseResultDecorator({
            scatterSymbolName: ApocalypseSymbolNames.SCATTER,
            finalDataReels: Apocalypse.model.currentSymbols,
            restrictedReelsByIndex: [0, 1, 2],
            gameReels: this.reels,
            timeline: this.flowPresentationTimeline,
            stopSpinDelay: stopDelay,
            showNearWinAnimationDelay: 0,
            nearWinDuration: normalConfig.nearWinDuration,
            hideNearWinDelay: normalConfig.hideNearWinDelay,
            wasFeatureBuy: this.model.gameData.wasFeatureBuy || this.model.data.enhancedBet,
            scatterLandingSoundName: `scatterLanding`,
            playSoundDelay: 0.18,
            onDecoratorComplete: this.handleFeatures.bind(this),
            gameMode: this.model.data.mode,
            reelsContainer: this.normalContainer,
        });

        if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            this.skip();
        }
    }

    protected handleFeatures() {
        this.initializeTimeline();

        if (Apocalypse.model.lostRounds === 10) {
            Apocalypse.model.lostRounds = 0;
            EventsManager.trigger(SHOW_EASTER_EGG_ANIMATION);
        }

        this.reels.forEach((reel, reelIndex) => {
            if (Apocalypse.model.xNudgeMap[reelIndex]) {
                this.flowPresentationTimeline.add(this.getXNudgeRevealAnimation(reelIndex));
                this.flowPresentationTimeline.addLabel(
                    `xNudgeEndLabel_` + reelIndex.toString(),
                    `>-0.1`
                );
            } else if (reelIndex === 3 && Apocalypse.model.hasWildsToNudge) {
                // ! mai trebuie un if reel index === 3 sau 4 si e parte dintr-un way sa faca animatia de win si idle
                // ! doar daca e SI insotit de un nudge - nudge map, altfel nu, ca se rezolva in win presentation
                // ! sau putem face animatia de win  + idle orice-ar fi. hai ca vedem

                const superXNudgeAnimation = this.getSuperXNudgeAnimation();
                // const superXnudgeAnimationTotalDuration = superXNudgeAnimation.totalDuration();

                this.flowPresentationTimeline.add(superXNudgeAnimation);
                this.flowPresentationTimeline.addLabel(
                    `sxNudgeEndLabel` + reelIndex.toString(),
                    `>-0.1`
                );
            }
        });

        if (this.specialSymbols.length > 0 && this.model.gameData.totalSpinWinnings === 0) {
            const stopSpecialSymbolsIdleAnimations = () => {
                this.specialSymbols.forEach((symbol) => {
                    if (symbol instanceof ApocalypseWildSymbol) {
                        symbol.getStaticAnimation();
                    } else {
                        symbol.getStaticAnimation();
                    }
                });
            };

            const stopIdleAnimationsLabel = `stopXNudgeIdleAnimationsLabel`;

            this.flowPresentationTimeline.addLabel(stopIdleAnimationsLabel, `>`);

            this.flowPresentationTimeline.call(
                () => {
                    stopSpecialSymbolsIdleAnimations();
                },
                undefined,
                stopIdleAnimationsLabel
            );

            this.flowPresentationTimeline.timeScale(2);
        }

        // const tlDuration = this.flowPresentationTimeline.duration();
        // const tlTotalDuration = this.flowPresentationTimeline.totalDuration();
        // const tlTimeScale = this.flowPresentationTimeline.timeScale();
        //
        // console.log(`salam`);

        this.flowPresentationTimeline.resume();
    }

    private getXNudgeRevealAnimation(reelIndex: number): gsap.core.Timeline {
        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                // this.addSkip(tl);
                addSkip(this.flowPresentationTimeline, this.controller);
                this.flowPresentationTimeline.timeScale(2);
                // const tlDuration = this.flowPresentationTimeline.duration();
                // const tlTotalDuration = this.flowPresentationTimeline.totalDuration();
                // const tlTimeScale = this.flowPresentationTimeline.timeScale();
                //
                // console.log(`salam`);
            },
        });

        this.specialSymbols.push(this.reels[reelIndex].getXNudgeSymbol());

        let hasMoreXNudges = Apocalypse.model.hasWildsToNudge && reelIndex < 2;
        for (let i = 0; i < Apocalypse.model.xNudgeMap.length; i++) {
            if (i > reelIndex && Apocalypse.model.xNudgeMap[i]) {
                hasMoreXNudges = true;
                break;
            }
        }

        tl.add(this.reels[reelIndex].getXNudgeAnimation(hasMoreXNudges));

        tl.addLabel(`onXNudgeAnimationLabelEnd`, `>-=0.1`);

        return tl;
    }

    private getSuperXNudgeAnimation(): gsap.core.Timeline {
        const secondWildSymbol = this.reels[3]
            .getAllVisibleStopSymbols()
            .find((symbol) => symbol.name === `WILD`) as ApocalypseWildSymbol;

        this.specialSymbols.push(secondWildSymbol);

        let hasMoreXNudges = false;
        for (let i = 0; i < Apocalypse.model.xNudgeMap.length; i++) {
            if (i > 3 && Apocalypse.model.xNudgeMap[i]) {
                hasMoreXNudges = true;
                break;
            }
        }

        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                addSkip(this.flowPresentationTimeline, this.controller);
                this.flowPresentationTimeline.timeScale(1);
            },
            onComplete: () => {
                if (hasMoreXNudges) {
                    secondWildSymbol.getIdleAnimation();
                }
            },
        });

        // tl.add(firstWildSymbol.getSuperXNudgeAnimation(), 0);
        tl.add(secondWildSymbol.getSuperXNudgeAnimation(), 0);

        tl.addLabel(`onSXNudgeAnimationLabelEnd`, `>-=0.2`);

        ///////
        let winValue = this.model.gameData.totalSpinWinnings;
        let winMultiplier: number = winValue / this.model.gameData.playedBetValue;
        const winLevel: IWinLevel = getWinLevel(winMultiplier);
        const winType: WinType = winLevel.winType;

        if (winType === WinType.BIG || winType === WinType.MAX_CAP) {
            let intensity = 6;
            let positionFactor: number = 0;
            let winFactor: number = 0;

            switch (winType) {
                case WinType.BIG: {
                    positionFactor = (5 - secondWildSymbol.positionInArray) * 0.2;
                    winFactor = +winLevel.name.replace(/^\D+/g, ``) * 0.1;

                    break;
                }
                default:
                    positionFactor = (5 - secondWildSymbol.positionInArray) * 0.3;
                    winFactor = 0.6;
                    break;
            }

            intensity += positionFactor + winFactor;

            tl.call(
                () => {
                    EventsManager.trigger(SHAKE_NORMAL_SCREEN, intensity);
                },
                undefined,
                `>-0.1`
            );
        }
        ///////

        tl.timeScale(1.1);

        // const tlDuration = tl.duration();
        // const tlTotalDuration = tl.totalDuration();
        // const tlTimeScale = tl.timeScale();

        this.addSkip(tl);

        return tl;
    }

    protected override finishPresentation() {
        if (
            this.model.gameData.addedNumberOfFreespinsThisSpin > 0 &&
            this.model.gameData.mode !== AbstractGameMode.NORMAL &&
            !this.model.gameData.brokeTheBank
        ) {
            super.finishPresentation();
        } else {
            super.finishPresentation();
        }
    }

    protected addSkip(timeline: gsap.core.Timeline, callback: Function | null = null): void {
        const skipToNextLevel = () => {
            this.stopSoundsOnSkip();

            const nextLabel: string = timeline.nextLabel();

            timeline.seek(timeline.labels[nextLabel], false);

            callback !== null && callback();
        };

        this.controller?.setSkipCallback(skipToNextLevel);
    }

    protected skip(): void {
        super.skip();

        this.flowPresentationTimeline.timeScale(fsConfig.skipCurrentSymbolsTimeScale! / 2);

        // const tlDuration = this.flowPresentationTimeline.duration();
        // const tlTotalDuration = this.flowPresentationTimeline.totalDuration();
        // const tlTimeScale = this.flowPresentationTimeline.timeScale();
        //
        // console.log(`salam`);
    }

    protected stopSoundsOnSkip() {
        // stopSound({ name: ApocalypseSoundNames.WILD_CATCH_FISH_01 });
        // stopSound({ name: ApocalypseSoundNames.WILD_CATCH_FISH_02 });
        // stopSound({ name: ApocalypseSoundNames.WILD_CATCH_FISH_03 });
        // stopSound({ name: ApocalypseSoundNames.WILD_GOLD_COUNTER });
    }
}
